<script setup lang="ts">
import AmbassadorFlare from '@/components/common/AmbassadorFlare.vue'
import { apiErrorMessage } from '@/utils/apiError'
import { useRoute, useRouter } from 'vue-router'
import BaseBadge from '@/components/common/BaseBadge.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import BaseCard from '@/components/common/BaseCard.vue'
import ListingOrderCta from '@/components/listings/ListingOrderCta.vue'
import PageFooter from '@/components/common/PageFooter.vue'
import SectionTitle from '@/components/common/SectionTitle.vue'
import PageLayout from '@/components/layout/PageLayout.vue'
import PageHeader from '@/components/layout/PageHeader.vue'
import { Drawer, DrawerContent } from '@/components/ui/drawer'
import { onMounted, ref, computed } from 'vue'
import { useScrollReveal, useShare } from '@/composables'
import { useAuthStore } from '@/stores/auth'
import { useListingsStore } from '@/stores/listings'
import { useOrdersStore } from '@/stores/orders'
import { api } from '@/services/api'
import { UIAvatar, UIChip, UIListingGallery } from '@/components/ui'
import {PhCaretLeft, PhCaretRight, PhStar, PhCheck, PhClock, PhWarningCircle, PhFlag, PhMagnifyingGlass, PhPi, PhShareNetwork, PhRepeat, PhShoppingBag, PhShieldCheck} from '@phosphor-icons/vue'


const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const listingsStore = useListingsStore()
const ordersStore = useOrdersStore()

const { observe, isVisible, reduceMotion } = useScrollReveal({ threshold: 0.1 })
const { shareListing } = useShare()

const listingId = route.params?.id as string
const listing = ref<any>(null)
const isLoading = ref(true)
const loadError = ref<string | null>(null)
const showOrderModal = ref(false)
const orderLoading = ref(false)
const showReportModal = ref(false)
const reportReason = ref('')
const isReporting = ref(false)

const statusLabels: Record<string, string> = {
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  disputed: 'Disputed',
  rejected: 'Rejected',
  draft: 'Draft',
}

const statusColors: Record<string, string> = {
  active: 'emerald',
  paused: 'amber',
  completed: 'emerald',
  disputed: 'red',
  rejected: 'red',
  draft: 'slate',
}

async function fetchListing() {
  isLoading.value = true
  loadError.value = null
  try {
    listing.value = await listingsStore.fetchListing(listingId)
  } catch (err: any) {
    loadError.value = apiErrorMessage(err, 'Failed to load this listing')
  } finally {
    isLoading.value = false
  }
}

async function createOrder() {
  if (!authStore.isAuthenticated) {
    router.push({ path: '/login/', query: { redirect: route.fullPath } })
    return
  }

  orderLoading.value = true
  try {
    const order = await ordersStore.createOrder(listingId)
    showOrderModal.value = false
    router.push(`/order/${order.id}/`)
  } catch (err: any) {
    authStore.addToast('error', apiErrorMessage(err, 'Failed to create order'))
  } finally {
    orderLoading.value = false
  }
}

async function reportListing() {
  if (isReporting.value) return
  if (!reportReason.value.trim()) {
    authStore.addToast('error', 'Please describe the problem')
    return
  }
  isReporting.value = true
  try {
    await api.reportListing(listingId, reportReason.value.trim())
    authStore.addToast('success', 'Listing reported. Our moderation team will review it.')
    showReportModal.value = false
    reportReason.value = ''
  } catch (err: any) {
    if (!authStore.isAuthenticated) {
      router.push({ path: '/login/', query: { redirect: route.fullPath } })
    } else {
      authStore.addToast('error', apiErrorMessage(err, 'Failed to submit report'))
    }
  } finally {
    isReporting.value = false
  }
}

async function share() {
  if (!listing.value) return
  try {
    const result = await shareListing(listing.value.id, listing.value.title)
    if (result === 'copied') authStore.addToast('success', 'Listing link copied')
  } catch {
    authStore.addToast('error', 'Unable to share this listing')
  }
}

function formatPrice(price: number) {
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const canOrder = computed(() =>
  authStore.isAuthenticated &&
  listing.value?.seller.id !== authStore.user?.id &&
  listing.value?.status === 'active'
)

const isOwner = computed(() =>
  authStore.isAuthenticated &&
  listing.value?.seller.id === authStore.user?.id
)

const memberSinceYear = computed(() => {
  const date = new Date(listing.value?.seller?.createdAt)
  return Number.isNaN(date.getTime()) ? null : date.getFullYear()
})

onMounted(fetchListing)
</script>

<template>
  <PageLayout nav="browse">
    <template #navbar>
      <PageHeader title="Service Details" back="/browse/">
        <template #right>
          <BaseButton
            variant="secondary"
            size="sm"
            aria-label="Share listing"
            class="bg-surface/80 text-ink shadow-xs"
            @click="share"
          >
            <PhShareNetwork class="w-4 h-4 shrink-0" weight="bold" />
            <span class="hidden sm:inline">Share</span>
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            aria-label="Report listing"
            class="text-ink-5 hover:text-ink"
            @click="showReportModal = true"
          >
            <PhFlag class="w-4 h-4 shrink-0" />
          </BaseButton>
        </template>
      </PageHeader>
    </template>

    <!-- Loading State -->
    <div v-if="isLoading" class="px-4 space-y-4">
      <div class="aspect-video bg-surface-2 rounded-2xl animate-pulse" />
      <div class="h-8 bg-surface-2 rounded-xl w-3/4 animate-pulse" />
      <div class="h-6 bg-surface-2 rounded-xl w-1/2 animate-pulse" />
      <div class="space-y-3 pt-4 border-t border-line">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-surface-2 rounded-full animate-pulse" />
          <div class="flex-1 space-y-2">
            <div class="h-4 bg-surface-2 rounded w-1/3 animate-pulse" />
            <div class="h-3 bg-surface-2 rounded w-1/4 animate-pulse" />
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div v-else-if="listing" class="mx-auto w-full max-w-7xl lg:grid lg:grid-cols-3 lg:items-start lg:gap-8 lg:px-8 lg:pt-8">
     <div class="lg:col-span-2 lg:min-w-0">
      <!-- md+: the desktop nav replaces PageHeader, so its actions live here -->
      <div class="hidden items-center justify-between px-6 pb-3 pt-6 md:flex lg:px-0 lg:pt-0">
        <BaseButton variant="ghost" size="sm" @click="() => router.push('/browse/')">
          <PhCaretLeft class="mr-1 h-4 w-4" /> Back to Browse
        </BaseButton>
        <div class="flex items-center gap-2">
          <BaseButton
            variant="secondary"
            size="sm"
            aria-label="Share listing"
            class="bg-surface/80 text-ink shadow-xs"
            @click="share"
          >
            <PhShareNetwork class="h-4 w-4 shrink-0" weight="bold" />
            <span>Share</span>
          </BaseButton>
          <BaseButton
            variant="ghost"
            size="sm"
            aria-label="Report listing"
            class="text-ink-5 hover:text-ink"
            @click="showReportModal = true"
          >
            <PhFlag class="h-4 w-4 shrink-0" />
          </BaseButton>
        </div>
      </div>
      <!-- Image Gallery -->
      <UIListingGallery
        v-if="listing.images.length > 0"
        v-observe="observe"
        :images="listing.images"
        :initial-index="0"
        class="lg:overflow-hidden lg:rounded-2xl"
      />
      <div v-else class="aspect-video bg-surface-2 flex items-center justify-center text-6xl lg:rounded-2xl">
        📦
      </div>

      <!-- Details Content -->
      <div class="shell-user px-4 pb-4 pt-6 lg:max-w-none lg:px-0">
        <!-- Title & Category -->
        <div class="mb-6" v-observe="observe" :class="{ 'opacity-0 translate-y-4': !reduceMotion && !isVisible.has($el) }">
          <div class="flex flex-wrap items-center gap-2 mb-3">
            <UIChip :label="listing.category.name" variant="outline" size="small" />
            <UIChip
              :label="`${listing.deliveryDays}d delivery`"
              variant="default"
              size="small"
              :icon="PhClock"
            />
            <UIChip
              :label="`${listing.revisionsIncluded} revision${listing.revisionsIncluded !== 1 ? 's' : ''} included`"
              variant="default"
              size="small"
              :icon="PhRepeat"
            />
            <UIChip
              v-if="listing.salesCount > 0"
              :label="`${listing.salesCount} sold`"
              variant="default"
              size="small"
              :icon="PhShoppingBag"
            />
          </div>

          <h1 class="text-2xl sm:text-3xl font-bold text-ink">{{ listing.title }}</h1>
        </div>

        <!-- Seller Info -->
        <BaseCard padding="none" class="mb-6 bg-surface/50 border-line" v-observe="observe" :class="{ 'opacity-0 translate-y-4': !reduceMotion && !isVisible.has($el) }">
          <div class="p-4">
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div class="flex items-center gap-4">
                <UIAvatar
                  :src="listing.seller.avatarURL"
                  :fallback="listing.seller.username"
                  size="lg"
                />
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <h3 class="font-semibold text-ink">{{ listing.seller.username }}</h3>
                    <AmbassadorFlare v-if="listing.seller.isAmbassador" labelled />
                    <BaseBadge
                      v-if="listing.seller.kycStatus === 'verified'"
                      color="emerald"
                    >
                      <PhCheck class="w-3 h-3 mr-1" />
                      Verified
                    </BaseBadge>
                  </div>
                  <div class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-ink-5">
                    <span class="flex items-center gap-1">
                      <PhStar class="w-4 h-4 text-accent-amber fill-amber-400" />
                      {{ listing.seller.ratingAvg.toFixed(1) }} ({{ listing.seller.ratingCount }})
                    </span>
                    <span class="flex items-center gap-1">
                      <PhCheck class="w-4 h-4 text-accent-emerald" />
                      {{ listing.seller.completionRate.toFixed(0) }}% completion
                    </span>
                    <span v-if="memberSinceYear" class="flex items-center gap-1">
                      <PhClock class="w-4 h-4" />
                      Member since {{ memberSinceYear }}
                    </span>
                  </div>
                </div>
              </div>
              <BaseButton
                variant="secondary"
                size="sm"
                @click="() => router.push(`/u/${encodeURIComponent(listing.seller.username)}/`)"
                class="self-start sm:self-auto"
              >
                View Profile
                <PhCaretRight class="w-4 h-4 ml-2" />
              </BaseButton>
            </div>
          </div>
        </BaseCard>

        <!-- Description -->
        <div class="mb-6" v-observe="observe" :class="{ 'opacity-0 translate-y-4': !reduceMotion && !isVisible.has($el) }">
          <SectionTitle class="text-ink mb-3">Description</SectionTitle>
          <div class="prose prose-invert max-w-none text-ink-4 whitespace-pre-wrap leading-relaxed">
            {{ listing.description }}
          </div>
        </div>

        <!-- Requirements -->
        <div v-if="listing.requirements" class="mb-6" v-observe="observe" :class="{ 'opacity-0 translate-y-4': !reduceMotion && !isVisible.has($el) }">
          <SectionTitle class="text-ink mb-3">Requirements</SectionTitle>
          <div class="prose prose-invert max-w-none text-ink-4 whitespace-pre-wrap leading-relaxed bg-surface/50 border border-line rounded-xl p-4">
            {{ listing.requirements }}
          </div>
        </div>

        <!-- Tags -->
        <div v-if="listing.tags.length > 0" class="mb-6" v-observe="observe" :class="{ 'opacity-0 translate-y-4': !reduceMotion && !isVisible.has($el) }">
          <SectionTitle class="text-ink mb-3">Skills & Tags</SectionTitle>
          <div class="flex flex-wrap gap-2">
            <UIChip
              v-for="tag in listing.tags"
              :key="tag"
              :label="tag"
              variant="outline"
              size="small"
            />
          </div>
        </div>

        <!-- Delivery Info -->
        <BaseCard padding="none" class="bg-surface/50 border-line" v-observe="observe" :class="{ 'opacity-0 translate-y-4': !reduceMotion && !isVisible.has($el) }">
          <div class="p-4">
            <div class="grid grid-cols-2 gap-4">
              <div class="text-center">
                <div class="w-12 h-12 mx-auto mb-2 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                  <PhClock class="w-6 h-6 text-accent-cyan" />
                </div>
                <p class="text-xs text-ink-5 uppercase tracking-wider">Delivery Time</p>
                <p class="font-semibold text-ink">{{ listing.deliveryDays }} days</p>
              </div>
              <div class="text-center">
                <div class="w-12 h-12 mx-auto mb-2 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <PhRepeat class="w-6 h-6 text-accent-emerald" />
                </div>
                <p class="text-xs text-ink-5 uppercase tracking-wider">Revisions</p>
                <p class="font-semibold text-ink">{{ listing.revisionsIncluded }}</p>
              </div>
              <div class="text-center">
                <div class="w-12 h-12 mx-auto mb-2 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <PhPi class="w-6 h-6 text-accent-amber" />
                </div>
                <p class="text-xs text-ink-5 uppercase tracking-wider">Price</p>
                <p class="font-semibold text-ink">{{ formatPrice(listing.pricePi) }} Pi</p>
              </div>
              <div class="text-center">
                <div class="w-12 h-12 mx-auto mb-2 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <PhCaretRight class="w-6 h-6 text-accent-purple" />
                </div>
                <p class="text-xs text-ink-5 uppercase tracking-wider">Status</p>
                <BaseBadge :color="statusColors[listing.status]">
                  {{ statusLabels[listing.status] || listing.status }}
                </BaseBadge>
              </div>
            </div>
          </div>
        </BaseCard>
      </div>
     </div>

      <!-- Desktop purchase summary (phones use the bottom bar) -->
      <aside class="hidden lg:block">
        <div class="sticky top-6 space-y-5 rounded-2xl border border-line bg-surface p-6">
          <div>
            <span class="text-xs uppercase tracking-wider text-ink-6">Total Price</span>
            <p class="mt-1 flex items-center gap-1.5 text-3xl font-bold text-ink">
              <PhPi class="h-7 w-7 text-accent-amber" />
              {{ formatPrice(listing.pricePi) }} <span class="text-sm font-normal text-ink-5">Pi</span>
            </p>
          </div>

          <ul class="space-y-2 text-sm text-ink-4">
            <li class="flex items-center gap-2">
              <PhClock class="h-4 w-4 text-accent-cyan" /> {{ listing.deliveryDays }} day delivery
            </li>
            <li class="flex items-center gap-2">
              <PhRepeat class="h-4 w-4 text-accent-emerald" />
              {{ listing.revisionsIncluded }} revision{{ listing.revisionsIncluded !== 1 ? 's' : '' }} included
            </li>
          </ul>

          <div class="flex">
            <ListingOrderCta
              :listing-id="listing.id"
              :can-order="canOrder"
              :is-owner="isOwner"
              :loading="orderLoading"
              @order="createOrder"
            />
          </div>

          <p class="flex items-start gap-2 rounded-xl bg-surface-2 p-3 text-xs text-ink-5">
            <PhShieldCheck class="mt-0.5 h-4 w-4 shrink-0 text-accent-emerald" />
            Escrow protected: your Pi is held until you approve the delivery.
          </p>
        </div>
      </aside>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-16 px-4">
      <component :is="loadError ? PhWarningCircle : PhMagnifyingGlass" class="w-16 h-16 mx-auto mb-4" :class="loadError ? 'text-accent-red' : 'text-ink-7'" />
      <p class="text-ink-5 mb-1">{{ loadError || 'Listing not found' }}</p>
      <p v-if="loadError" class="text-sm text-ink-6 mb-4">Check your connection and try again.</p>
      <div class="flex items-center justify-center gap-3">
        <BaseButton variant="accent" @click="fetchListing">Retry</BaseButton>
        <BaseButton variant="secondary" @click="() => router.push('/browse/')">Browse Services</BaseButton>
      </div>
    </div>

    <!-- Report Modal -->
    <Drawer
      v-model:open="showReportModal"
      swipe-direction="down"
      @update:open="(v) => { if (!v && !isReporting) showReportModal = false }"
    >
      <DrawerContent>
        <div class="p-4 space-y-4">
          <h2 class="text-base font-semibold text-ink">Report Listing</h2>
          <p class="text-ink-4">Help us keep TalentSwap safe. Describe the problem with this listing and our moderation team will review it.</p>
          <textarea
            v-model="reportReason"
            placeholder="Describe the issue..."
            rows="4"
            maxlength="1000"
            class="w-full bg-surface-2 border border-line-2 rounded-xl px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent resize-none"
          />
          <div class="flex gap-3 pt-2">
            <BaseButton variant="secondary" class="flex-1" :disabled="isReporting" @click="showReportModal = false">Cancel</BaseButton>
            <BaseButton variant="danger" class="flex-1" :loading="isReporting" :disabled="isReporting" @click="reportListing">Report</BaseButton>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
    <template #fixed>
      <!-- Sticky bottom order CTA -->
      <PageFooter v-if="listing && !isLoading" class="lg:hidden">
        <div class="flex items-center justify-between gap-4">
          <div class="flex flex-col">
            <span class="text-xs text-ink-6 uppercase tracking-wider">Total Price</span>
            <span class="text-xl font-bold text-ink flex items-center gap-1">
              <PhPi class="w-5 h-5 text-accent-amber" />
              {{ formatPrice(listing.pricePi) }} <span class="text-xs text-ink-5 font-normal">Pi</span>
            </span>
          </div>

          <ListingOrderCta
            :listing-id="listing.id"
            :can-order="canOrder"
            :is-owner="isOwner"
            :loading="orderLoading"
            @order="createOrder"
          />
        </div>
      </PageFooter>
    </template>
  </PageLayout>
</template>
